/**
 * 画布 / 导出图纸上的编号标签排布。
 *
 * 柜子画得近时，默认贴在框上方的编号会互相压住，看不清谁是谁。
 * 这里做一次贪心避让（纯函数，同样输入永远同样结果，不会来回闪）：
 *   1. 先贴框上方；压住了就一层层往上让；
 *   2. 上方让不开就放框下方，再不行退到框内左上角。
 * 当前选中的柜体优先占位，保证你在操作的框永远是默认位置、看得最清楚。
 *
 * 标签大小**按框体大小给**（每个输入自带 height / padding，见 PdfCanvas）：
 * 大柜子的编号大、小柜子的编号小，柜子再小也不会被编号糊住。
 */

export type LabelInput = {
  id: string;
  /** 框在页面里的位置与大小（同一坐标系，通常是页面 px） */
  x: number;
  y: number;
  w: number;
  h: number;
  /** 编号文字宽度（已按当前字号量好） */
  numberWidth: number;
  /** 徽标总宽度（"未填尺寸"这类，始终显示） */
  badgeWidth?: number;
  /** 这个标签自己的高度（按框体大小算出来）；不传就用 options.height */
  height?: number;
  /** 这个标签自己的内边距；不传就用 options.padding */
  padding?: number;
};

export type LabelPlacement = {
  /** 相对框左上角的偏移 */
  dx: number;
  dy: number;
  width: number;
  height: number;
  /** 有没有找到不打架的位置（都占满时退回框内） */
  clear: boolean;
};

export type LabelLayoutOptions = {
  pageWidth: number;
  pageHeight: number;
  selectedId?: string | null;
  /** 标签左右内边距（一个 chip 的 px-1.5 就是 6） */
  padding: number;
  /** 标签高度 */
  height: number;
  /** 判定重叠时额外留的余量 */
  margin?: number;
};

/** 两个矩形是否（几乎）相交 */
function overlaps(
  a: { x: number; y: number; w: number; h: number },
  b: { x: number; y: number; w: number; h: number },
  margin: number,
): boolean {
  return (
    a.x < b.x + b.w + margin &&
    b.x < a.x + a.w + margin &&
    a.y < b.y + b.h + margin &&
    b.y < a.y + a.h + margin
  );
}

export function layoutBoxLabels(
  inputs: LabelInput[],
  options: LabelLayoutOptions,
): Record<string, LabelPlacement> {
  const { pageWidth, pageHeight, selectedId, padding, height } = options;
  const margin = options.margin ?? 2;
  const result: Record<string, LabelPlacement> = {};
  if (inputs.length === 0 || pageWidth <= 0 || pageHeight <= 0) return result;

  // 标签自身的宽度：编号 + 内边距 + 徽标（"未填尺寸"这类）
  const widthOf = (input: LabelInput) =>
    input.numberWidth + (input.padding ?? padding) * 2 + (input.badgeWidth ?? 0);

  const placed: Array<{ x: number; y: number; w: number; h: number }> = [];

  // 选中的先排；其余按"从上到下、从左到右"的阅读顺序
  const ordered = [...inputs].sort((a, b) => {
    if (a.id === selectedId) return -1;
    if (b.id === selectedId) return 1;
    return a.y - b.y || a.x - b.x;
  });

  for (const input of ordered) {
    const inputHeight = input.height ?? height;
    const above = -inputHeight - 4;
    const candidates: Array<{ dx: number; dy: number }> = [
      { dx: 0, dy: above },
      { dx: 0, dy: above - (inputHeight + 4) },
      { dx: 0, dy: above - (inputHeight + 4) * 2 },
      { dx: 0, dy: above - (inputHeight + 4) * 3 },
      { dx: 0, dy: input.h + 6 }, // 框下方
      { dx: 3, dy: 3 }, // 框内左上角（最后退路）
    ];

    let chosen: LabelPlacement | null = null;
    for (const candidate of candidates) {
      const width = widthOf(input);
      const x = Math.min(Math.max(input.x + candidate.dx, 0), Math.max(0, pageWidth - width));
      const y = input.y + candidate.dy;
      const rect = { x, y, w: width, h: inputHeight };
      const insidePage = y >= 0 && y + inputHeight <= pageHeight;
      const clear = insidePage && !placed.some((other) => overlaps(rect, other, margin));
      if (clear) {
        chosen = { ...candidate, width, height: inputHeight, clear: true };
        placed.push(rect);
        break;
      }
      // 最后一个候选（框内）无论如何都用，避免标签凭空消失
      if (candidate === candidates[candidates.length - 1]) {
        chosen = { ...candidate, width, height: inputHeight, clear: false };
        placed.push(rect);
      }
    }
    if (chosen) {
      result[input.id] = chosen;
    }
  }

  return result;
}
